/**
 * 작성자 : 박승호
 * 날짜 : 2025.01.07
 * 경로 : 사내용 상황판 > 시정사항 & 조치내용 정보
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { apiGet } from '../../services/_common';
import IssueGuide from '../IssueGuide';
import SkeletonTable from '../SkeletonTable';
import { useEffect, useMemo, useRef, useState } from 'react';
import { CustomDot } from './Carousel';
import Carousel from 'react-multi-carousel';
import TextEditor from '../TextEditor';
import dayjs from 'dayjs';

const Root = styled.div`
  user-select: none;
  height: 100%;

  > .widget-headers {
    width: 100%;
    padding: 0.75rem 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    flex-grow: 0;

    &.margin-top {
      margin-bottom: 0;
    }
    > .widget-title {
      padding: 0 1rem;
      align-items: center;
    }
  }

  > .widget-body {
    &.card {
      width: 100%;
      height: calc(100% - 3.5rem);
      padding: 0 1rem 1rem;
      flex-grow: 1;

      > .carousel-container {
        height: 100%;

        .react-multi-carousel-list,
        .react-multi-carousel-track {
          height: 100%;
        }
      }

      .correct-wrap {
        display: flex;
        flex-direction: column;
        height: 90%;

        .correct-top {
          height: 50%;
        }

        .correct-bottom {
          display: flex;
          flex-direction: column;
          height: 50%;

          .correct-header {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            flex-grow: 0;
            width: 100%;
            padding: 1.5rem 0 0.75rem;

            > span {
              flex-shrink: 0;
              font-weight: var(--font-semibold);
              font-size: 1.125rem;
              line-height: 1.2;
              color: var(--text-primary);
              display: flex;
              align-items: center;
            }
          }

          .correct-write-info {
            padding-bottom: 0.5rem;
          }
        }

        .correct-item {
          display: flex;
          gap: 0.5rem;
          width: 100%;
          height: 100%;
          padding: 0.5rem;
          border: 1px solid var(--outline);
          border-radius: 0.5rem;

          .correct-img-cont {
            width: 50%;
            height: 100%;
            border-radius: 0.5rem;
            overflow: hidden;

            img.correct-img {
              display: block;
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            .issueBox {
              background-color: var(--tonal-light);
            }

            > .carousel-container {
              height: 100%;

              .react-multi-carousel-list,
              .react-multi-carousel-track {
                height: 100%;
              }
            }
          }

          .correct-content-info {
            display: flex;
            flex-direction: column;
            width: 50%;
            height: 100%;
            padding: 0.5rem;

            .correct-tit-cont {
              padding: 0.5rem 0;
            }

            .correct-write-info {
              color: #666;

              span {
                font-size: 0.75rem;
                line-height: 1.2;
              }
            }

            h4 {
              color: var(--text-primary);
              font-size: 1.125rem;
              line-height: 1.2;
            }
          }
        }
      }

      /* ckeditor custom style */
      .ck-editor-cont {
        width: 100%;
        height: 100%;
        overflow-y: hidden;
        border-top: 1px solid var(--outline);

        .ckeditor {
          height: 100%;

          /* 에디터 상단 툴바 제거 */
          .ck-editor__top {
            display: none;
          }

          .ck-editor,
          .ck-editor__main {
            height: 100%;
            li {
              white-space: normal;
            }
          }

          .ck-rounded-corners .ck.ck-editor__main > .ck-editor__editable,
          .ck.ck-editor__main > .ck-editor__editable.ck-rounded-corners {
            // height: 100%;
            border: none;
            padding: 0;
            // overflow: hidden;
            white-space: normal;
          }
        }
      }
    }
  }

  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    padding: 9rem 0;
    border: 1px solid var(--outline);
    border-radius: 0.5rem;
  }
`;

interface Iprops {
  name: string;
  userInfo: any;
  refreshTimeCount: number;
}

const ANIMATION_DELAY = 3; // 애니메이션 딜레이 시간 / 3 = 3초;

const CorrectInfo = ({ name, userInfo, refreshTimeCount }: Iprops) => {
  const { t } = useTranslation();
  const ckRefs = useRef<(HTMLDivElement | null)[]>([]);
  const ckRefs2 = useRef<(HTMLDivElement | null)[]>([]);

  const [curIdx, setCurIdx] = useState<number>(0); // carousel 현재 인덱스 - carousel 슬라이드 변경시 애니메이션 초기화를 위해 사용

  const {
    data: corrects,
    isLoading,
    isError,
    refetch,
  } = useQuery(['/safe/correctboard_dashboard'], () => getCorrectAPI(), {
    enabled: !!userInfo.hCd,
    refetchInterval: refreshTimeCount * 1000, // 30분마다 재호출
    refetchIntervalInBackground: true, // 포커싱 안되어 있어도 refetchInterval 기능 수행
  });

  const getCorrectAPI = async () => {
    const req = { cCd: userInfo.hCd };
    const res = await apiGet({ path: '/safe/correctboard', req });
    const { statusCode, data } = res.data;

    if (statusCode === 200) {
      const filterYn = data.filter((el: any) => el.cbtDefault === 'Y'); // 상황판 사용 유무가 Y인 것만 필터링
      return filterYn;
    }

    return [];
  };

  useEffect(() => {
    let handleAnimationEnd1: (event: AnimationEvent) => void;
    let handleAnimationEnd2: (event: AnimationEvent) => void;
    let targetElement1: HTMLDivElement;
    let targetElement2: HTMLDivElement;

    // 시정사항 애니메이션
    const checkElement1 = () => {
      targetElement1 = document.querySelector('.react-multi-carousel-item--active > .correct-wrap > .rectify-wrap .ckeditor') as HTMLDivElement;

      if (targetElement1) {
        const viewHeight = Math.floor(document.querySelector('.react-multi-carousel-item--active > .correct-wrap > .rectify-wrap .ck-editor-cont')?.getBoundingClientRect().height || 0); // 뷰 높이
        const contentHeight = targetElement1?.scrollHeight || 0; // 내용 전체 높이
        const fmtSecond = (contentHeight * 0.05).toFixed(2);
        const contentAnimation = contentHeight > viewHeight - 8 ? `slide ${fmtSecond}s ${ANIMATION_DELAY}s linear` : 'none';

        targetElement1.style.animation = contentAnimation; // 애니메이션 적용

        // 애니메이션 종료 시 실행되는 이벤트 핸들러
        handleAnimationEnd1 = () => {
          targetElement1.style.animation = 'none'; // 애니메이션 제거
          const reflow = targetElement1.offsetHeight; // 리플로우 강제 발생 - 이렇게 안하면 재시작이 안됨
          targetElement1.style.animation = contentAnimation; // 애니메이션 다시 적용
        };

        targetElement1.addEventListener('animationend', handleAnimationEnd1);
      } else {
        // 요소를 찾지 못했을 때 요소 탐색 재시도 - caurosel 라이브러리 사용시 로드될 때 요소를 못찾기 때문
        setTimeout(checkElement1, 100);
      }
    };

    // 조치내용 애니메이션
    const checkElement2 = () => {
      targetElement2 = document.querySelector('.react-multi-carousel-item--active > .correct-wrap > .action-wrap .ckeditor') as HTMLDivElement;

      if (targetElement2) {
        const viewHeight = Math.floor(document.querySelector('.react-multi-carousel-item--active > .correct-wrap > .action-wrap .ck-editor-cont')?.getBoundingClientRect().height || 0); // 뷰 높이
        const contentHeight = targetElement2?.scrollHeight || 0; // 내용 전체 높이
        const fmtSecond = (contentHeight * 0.05).toFixed(2);
        const contentAnimation = contentHeight > viewHeight - 8 ? `slide ${fmtSecond}s ${ANIMATION_DELAY}s linear` : 'none';

        targetElement2.style.animation = contentAnimation; // 애니메이션 적용

        // 애니메이션 종료 시 실행되는 이벤트 핸들러
        handleAnimationEnd2 = () => {
          targetElement2.style.animation = 'none'; // 애니메이션 제거
          const reflow = targetElement2.offsetHeight; // 리플로우 강제 발생 - 이렇게 안하면 재시작이 안됨
          targetElement2.style.animation = contentAnimation; // 애니메이션 다시 적용
        };

        targetElement2.addEventListener('animationend', handleAnimationEnd1);
      } else {
        // 요소를 찾지 못했을 때 요소 탐색 재시도 - caurosel 라이브러리 사용시 로드될 때 요소를 못찾기 때문
        setTimeout(checkElement1, 100);
      }
    };

    checkElement1();
    checkElement2();

    return () => {
      if (targetElement1 && handleAnimationEnd1) {
        targetElement1.removeEventListener('animationend', handleAnimationEnd1);
      }
      if (targetElement2 && handleAnimationEnd2) {
        targetElement2.removeEventListener('animationend', handleAnimationEnd2);
      }
    };
  }, [curIdx]);

  const responsive = useMemo(
    () => ({
      desktop: {
        breakpoint: { max: 3000, min: 1024 },
        items: 1,
        // items: size === 'large' ? carouselCount : 1,
        slidesToSlide: 1, // optional, default to 1.
      },
      tablet: {
        breakpoint: { max: 1024, min: 464 },
        items: 1,
        // items: size === 'large' ? 2 : 1,
        slidesToSlide: 1, // optional, default to 1.
      },
      mobile: {
        breakpoint: { max: 464, min: 0 },
        items: 1,
        slidesToSlide: 1, // optional, default to 1.
      },
    }),
    []
  );

  const loading = !corrects || isLoading;
  if (isError) return <IssueGuide />;

  return (
    <Root>
      <div className='widget-headers'>
        <div className='widget-title flex-between'>
          <span>
            {t(name)}
            <span className='badge'>{corrects?.length ?? 0}</span>
          </span>
        </div>
      </div>
      {loading ? (
        <div style={{ width: '100%', height: '100%' }}>
          <SkeletonTable rows={3} />
        </div>
      ) : (
        <div className='widget-body card'>
          <Carousel
            swipeable
            draggable
            responsive={responsive}
            infinite
            autoPlay={corrects?.length > 1}
            autoPlaySpeed={60000}
            keyBoardControl
            transitionDuration={500}
            containerClass='carousel-container'
            removeArrowOnDeviceType={['tablet', 'mobile', 'desktop']}
            dotListClass='custom-dot-list-style'
            showDots
            customDot={<CustomDot mergedData={corrects} />}
            afterChange={(previousSlide, { currentSlide, onMove }: any) => {
              setCurIdx(currentSlide);

              // Carousel 슬라이드 변경시 애니메이션 초기화
              const targetAll1: NodeListOf<HTMLDivElement> | null = document.querySelectorAll('.rectify-wrap');
              const targetAll2: NodeListOf<HTMLDivElement> | null = document.querySelectorAll('.action-wrap');

              if (targetAll1) {
                targetAll1.forEach((nodeEl: HTMLDivElement) => {
                  const childNode = nodeEl.querySelector('.ckeditor') as HTMLDivElement | null;
                  if (childNode) childNode.style.animation = 'none';
                });
              }

              if (targetAll2) {
                targetAll2.forEach((nodeEl: HTMLDivElement) => {
                  const childNode = nodeEl.querySelector('.ckeditor') as HTMLDivElement | null;
                  if (childNode) childNode.style.animation = 'none';
                });
              }
            }}
          >
            {corrects.map((correct: any, index: number) => (
              <div key={correct.cbtCd} className='correct-wrap'>
                <div className='correct-top rectify-wrap'>
                  <div className='correct-item'>
                    <div className='correct-img-cont'>
                      {correct.boardD.length > 0 ? (
                        <Carousel
                          swipeable={false}
                          draggable={false}
                          responsive={responsive}
                          infinite
                          autoPlay={correct.boardD.length > 1}
                          autoPlaySpeed={5000}
                          keyBoardControl={false}
                          transitionDuration={500}
                          containerClass='carousel-container'
                          removeArrowOnDeviceType={['tablet', 'mobile', 'desktop']}
                          dotListClass='custom-dot-list-style'
                          showDots
                          customDot={<CustomDot mergedData={correct.boardD} />}
                        >
                          {correct.boardD.map((img: any) => (
                            <img key={img.cbdCd} src={img.cFilename} alt={img.cFilename} className='correct-img' />
                          ))}
                        </Carousel>
                      ) : (
                        <IssueGuide text='이미지가 없습니다' type='img' />
                      )}
                    </div>
                    <div className='correct-content-info'>
                      <div className='correct-tit-cont'>
                        <h4>{correct.cbtTitle}</h4>
                        <div className='correct-write-info'>
                          <span>{dayjs(correct.wDate).format('YYYY-MM-DD')}</span>
                          <b> · </b>
                          <span>{correct.writer}</span>
                        </div>
                      </div>
                      <div className='ck-editor-cont'>
                        <div
                          ref={(item: any) => {
                            ckRefs.current[index] = item;
                          }}
                        >
                          <TextEditor content={correct.cbtContents} readOnly />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='correct-bottom action-wrap'>
                  <div className='correct-header'>
                    <span>{t('조치 내용')}</span>
                  </div>

                  {correct.cbtRcontents !== '' ? (
                    <div className='correct-item'>
                      <div className='correct-img-cont'>
                        {correct.rBoardD.length > 0 ? (
                          <Carousel
                            swipeable={false}
                            draggable={false}
                            responsive={responsive}
                            infinite
                            autoPlay={correct.rBoardD.length > 1}
                            autoPlaySpeed={5000}
                            keyBoardControl={false}
                            transitionDuration={500}
                            containerClass='carousel-container'
                            removeArrowOnDeviceType={['tablet', 'mobile', 'desktop']}
                            dotListClass='custom-dot-list-style'
                            showDots
                            customDot={<CustomDot mergedData={correct.rBoardD} />}
                          >
                            {correct.rBoardD.map((img: any) => (
                              <img key={img.cbdCd} src={img.cFilename} alt={img.cFilename} className='correct-img' />
                            ))}
                          </Carousel>
                        ) : (
                          <IssueGuide text='이미지가 없습니다' type='img' />
                        )}
                      </div>
                      <div className='correct-content-info'>
                        <div>
                          <div className='correct-write-info'>
                            <span>{dayjs(correct.rwDate).format('YYYY-MM-DD')}</span>
                            <b> · </b>
                            <span>{correct.rwriter}</span>
                          </div>
                        </div>
                        <div className='ck-editor-cont'>
                          <div
                            ref={(item: any) => {
                              ckRefs2.current[index] = item;
                            }}
                          >
                            <TextEditor content={correct.cbtRcontents} readOnly />
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className='no-data'>
                      <IssueGuide text='조치 내용이 없습니다' />
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Carousel>
        </div>
      )}
    </Root>
  );
};

export default CorrectInfo;
